import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import NotFound from "./NotFound";
import { get, post } from "../utils/api"; // your API helper
import { SquarePen, Search, Filter, Download, Plus } from "lucide-react";

import { InputText } from "primereact/inputtext";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomDialog from "../components/dialog/CustomDialog";
import { Check } from "lucide-react";

const RoleMasterPage = () => {
  const navigate = useNavigate();

  const userId = localStorage.getItem("user_details")
    ? JSON.parse(localStorage.getItem("user_details")).id
    : null;

  const [roles, setRoles] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    role_code: "",
    role_desc: "",
    is_active: true, // default true for create
  });
  const [formErrors, setFormErrors] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!userId) {
      navigate("/not", { replace: true });
    } else {
      fetchRoles();
    }
  }, [userId, navigate]);

  const fetchRoles = async () => {
    try {
      const { data } = await get("/role/fetchRoles");
      setRoles(data?.Data || []);
    } catch (err) {
      console.error(err);
      toast.error("Error fetching roles.");
    }
  };

  const openCreateModal = () => {
    setFormErrors("");
    setFormData({
      id: null,
      role_code: "",
      role_desc: "",
      is_active: true, // always true on create
    });
    setModalVisible(true);
  };

  const openEditModal = (role) => {
    setFormErrors("");
    setFormData({
      id: role.id,
      role_code: role.role_code || "",
      role_desc: role.role_desc || "",
      is_active: role.is_active,
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const downloadCSV = () => {
    if (!roles || roles.length === 0) {
      toast.warning("No data to download.");
      return;
    }

    const headers = ["ID", "Role Code", "Role Description", "Status"];
    const rows = roles.map((role) => [
      role.id,
      role.role_code,
      role.role_desc,
      role.is_active ? "Active" : "Inactive",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows]
        .map((e) => e.map((v) => `"${v ?? ""}"`).join(","))
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "role_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredRoles = useMemo(() => {
    if (!searchTerm) return roles;
    const lower = searchTerm.toLowerCase();
    return roles.filter(
      (role) =>
        (role.role_code && role.role_code.toLowerCase().includes(lower)) ||
        (role.role_desc && role.role_desc.toLowerCase().includes(lower)) ||
        (role.id && String(role.id).toLowerCase().includes(lower))
    );
  }, [roles, searchTerm]);

  // UPDATED handleChange to block special characters for role_code
  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    if (name === "role_code") {
      // Allow only letters and numbers (no special chars or spaces)
      newValue = newValue.replace(/[^a-zA-Z0-9]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({ ...prev, is_active: checked }));
  };

  const validateForm = () => {
    if (!formData.role_code.trim()) {
      setFormErrors("Role Code is required");
      return false;
    }
    if (!formData.role_desc.trim()) {
      setFormErrors("Role Description is required");
      return false;
    }
    setFormErrors("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const roleCodeUpper = formData.role_code.trim().toUpperCase();
    const roleDescTrimmed = formData.role_desc.trim();

    try {
      // Only do duplicate check if role code is new OR changed
      const original = roles.find((r) => r.id === formData.id);

      const isRoleCodeChanged =
        !formData.id || // new role
        (original && original.role_code.toUpperCase() !== roleCodeUpper);

      if (isRoleCodeChanged) {
        const dupCheckPayload = { role_code: roleCodeUpper };
        if (formData.id) {
          dupCheckPayload.id = formData.id;
        }

        const dupCheck = await post(
          "/role/checkDuplicateRole",
          dupCheckPayload
        );

        if (!dupCheck.data.Success) {
          setFormErrors("Role code already exists");
          return;
        }
      }

      // Proceed to create or update
      if (formData.id) {
        // Update existing
        await post("/role/updateRole", {
          id: formData.id,
          role_code: roleCodeUpper,
          role_desc: roleDescTrimmed,
          is_active: formData.is_active,
          user_Id: userId,
        });
        toast.success("Role updated successfully");
      } else {
        // Create new
        await post("/role/createRole", {
          role_code: roleCodeUpper,
          role_desc: roleDescTrimmed,
          is_active: true,
          user_Id: userId,
        });
        toast.success("Role created successfully");
      }

      closeModal();
      await fetchRoles();
    } catch (err) {
      console.error(err);
      toast.error("Error saving role.");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Action",
        Cell: ({ row }) => (
          <Button
            variant="ghost"
            className="text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 p-2 rounded-lg transition-all"
            onClick={() => openEditModal(row.original)}
            aria-label="Edit role"
          >
            <SquarePen size={18} />
          </Button>
        ),
        disableSortBy: true,
        disableFilters: true,
        size: 80,
      },
      {
        Header: "ID",
        accessor: "id",
        size: 80,
        Cell: ({ value }) => <span className="font-normal text-gray-900">{value}</span>
      },
      {
        Header: "Role Code",
        accessor: "role_code",
        size: 150,
        Cell: ({ value }) => <span className="font-normal text-gray-800 uppercase leading-tight inline-block max-w-[140px] break-words">{value}</span>
      },
      {
        Header: "Role Description",
        accessor: "role_desc",
        size: 200,
      },
      {
        Header: "Status",
        accessor: "is_active",
        Cell: ({ value }) => {
          const statusText = value ? "Active" : "Inactive";
          const bgColor = value ? "bg-[#EBFFF3]" : "bg-[#F3F4F6]";
          const textColor = value ? "text-[#10B981]" : "text-[#6B7280]";
          const dotColor = value ? "bg-[#10B981]" : "bg-[#9CA3AF]";

          return (
            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full border ${bgColor} ${textColor} text-[11px] font-normal w-fit mx-auto`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
              {statusText}
            </div>
          );
        },
        size: 150,
      },
    ],
    []
  );

  if (!userId) return <NotFound />;

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 h-full min-h-screen bg-gray-50/30">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800 tracking-tight">Role Management</h1>
        <p className="text-[11px] text-gray-400 font-medium tracking-wide">Configure system roles and access levels</p>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3 w-full max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by role code, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow bg-white h-[42px]"
            />
          </div>
          <button
            type="button"
            onClick={downloadCSV}
            className="flex items-center gap-2 text-gray-700 border border-gray-200 hover:bg-gray-50 bg-white h-[42px] px-4 rounded-lg font-medium shadow-sm transition-colors"
          >
            <Download className="w-4 h-4 text-gray-500" />
            <span className="text-sm">Export</span>
          </button>
        </div>

        <button
          className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-sm border-none h-[42px] px-5 rounded-lg transition-colors"
          onClick={openCreateModal}
        >
          <Plus className="w-4 h-4" />
          <span className="font-medium text-sm">Add Role</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <ReusableTable columns={columns} data={filteredRoles} />
      </div>

      {/* Modal for Add/Edit Role */}
      <CustomDialog
        visible={modalVisible}
        onHide={closeModal}
        header={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Plus className="w-4 h-4 text-indigo-600" />
            </div>
            <span className="text-lg font-bold text-gray-900">{formData.id ? "Edit Role" : "Add Role"}</span>
          </div>
        }
        width="40vw"
        closable={true}
      >
        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="role_code" className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
              Role Code
            </label>
            <InputText
              id="role_code"
              name="role_code"
              value={formData.role_code}
              onChange={handleChange}
              autoFocus
              className={`w-full px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white h-[42px] transition-shadow ${formData.id ? "bg-gray-50 cursor-not-allowed font-bold" : ""}`}
              maxLength={50}
              placeholder="Enter Role Code (e.g. ADMIN101)"
              readOnly={!!formData.id}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="role_desc" className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
              Role Description
            </label>
            <InputText
              id="role_desc"
              name="role_desc"
              value={formData.role_desc}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white h-[42px] transition-shadow"
              maxLength={100}
              placeholder="Enter Purpose of this role"
            />
          </div>

          {/* Show Active checkbox ONLY on edit */}
          {formData.id && (
            <div className="flex flex-col gap-2 mt-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Status</label>
              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <div
                  className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${formData.is_active ? "bg-indigo-500" : "bg-gray-200"
                    }`}
                  onClick={() => handleCheckboxChange({ target: { checked: !formData.is_active } })}
                >
                  <div
                    className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-transform ${formData.is_active ? "translate-x-6" : "translate-x-1"
                      }`}
                  />
                </div>
                <span className={`text-sm font-bold ${formData.is_active ? "text-indigo-600" : "text-gray-500"}`}>
                  {formData.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          )}

          {formErrors && (
            <p className="text-red-600 font-semibold">{formErrors}</p>
          )}

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-8">
            <button
              type="button"
              onClick={closeModal}
              className="px-6 py-2 text-sm font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 shadow-lg shadow-indigo-200 active:scale-95 transition-all flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              {formData.id ? "Update Role" : "Save Role"}
            </button>
          </div>
        </form>
      </CustomDialog>
    </div>
  );
};

export default RoleMasterPage;
